import React, { useState } from 'react';
import { FadeIn } from './FadeIn';
import { XIcon, ShieldCheckIcon, FileTextIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLanguage } from './LanguageContext';
function LanguageToggle() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const isSpanish = language === 'es';
  return <button onClick={() => setLanguage(isSpanish ? 'en' : 'es')} className="text-sm text-gray-500 hover:text-amber transition-colors duration-200 flex items-center gap-2" data-id="element-120">
      <span className="text-base" data-id="element-121">{isSpanish ? '🇺🇸' : '🇲🇽'}</span>
      {isSpanish ? 'Show in English' : 'Mostrar en Español'}
    </button>;
}
type ModalType = 'privacy' | 'terms' | null;
export function FooterSection() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const {
    t
  } = useLanguage();
  return <>
      <footer className="w-full bg-navy-900 border-t border-steel-dark/30 py-12" data-id="element-122">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-123">
          <FadeIn data-id="element-124">
            <div className="flex flex-col items-center text-center" data-id="element-125">
              <img src="https://cdn.magicpatterns.com/uploads/7A9LqtxuE6ks6URNjhDydm/logo_real_22.png" alt="ARCO Techos y Estructuras Logo" className="h-16 w-auto object-contain mb-6 opacity-80 hover:opacity-100 transition-opacity" data-id="element-126" />

              {/* Language toggle */}
              <div className="mb-6" data-id="element-127">
                <LanguageToggle data-id="element-128" />
              </div>

              <div className="text-gray-400 text-sm space-y-2 mb-6" data-id="element-129">
                <p data-id="element-130">itzelrojasramirez51@gmail.com</p>
                <p data-id="element-131">
                  Calle 2 de Abril #134, Colonia Antonio de León, Huajuapan de
                  León, Oaxaca
                </p>
              </div>

              {/* Privacy & Terms links */}
              <div className="flex items-center gap-4 mb-6" data-id="element-132">
                <button onClick={() => setActiveModal('privacy')} className="text-sm text-gray-500 hover:text-amber transition-colors duration-200 underline underline-offset-2" data-id="element-133">
                  {t('footer.privacy')}
                </button>
                <span className="text-gray-700" data-id="element-134">|</span>
                <button onClick={() => setActiveModal('terms')} className="text-sm text-gray-500 hover:text-amber transition-colors duration-200 underline underline-offset-2" data-id="element-135">
                  {t('footer.terms')}
                </button>
              </div>

              <div className="text-xs text-gray-600" data-id="element-136">
                &copy; {new Date().getFullYear()} ARCO Techos y Estructuras de
                la Mixteca. {t('footer.rights')}
              </div>
            </div>
          </FadeIn>
        </div>
      </footer>

      {/* Modal overlay */}
      <AnimatePresence data-id="element-137">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-138">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="relative w-full max-w-2xl max-h-[80vh] bg-navy-900 border border-steel-dark/50 rounded-xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-139">
              {/* Modal header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-steel-dark/30 bg-navy-900/80" data-id="element-140">
                <div className="flex items-center gap-3" data-id="element-141">
                  {activeModal === 'privacy' ? <ShieldCheckIcon className="w-5 h-5 text-amber" data-id="element-142" /> : <FileTextIcon className="w-5 h-5 text-amber" data-id="element-143" />}
                  <h2 className="text-lg font-semibold text-white font-heading" data-id="element-144">
                    {activeModal === 'privacy' ? t('footer.privacy') : t('footer.terms')}
                  </h2>
                </div>
                <button onClick={() => setActiveModal(null)} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors" aria-label={t('footer.close')} data-id="element-145">
                  <XIcon className="w-5 h-5" data-id="element-146" />
                </button>
              </div>

              {/* Modal content */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-72px)] text-gray-300 text-sm leading-relaxed space-y-4" data-id="element-147">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-148" /> : <TermsContent data-id="element-149" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  const {
    t
  } = useLanguage();
  return <>
      <p className="text-gray-400 text-xs" data-id="element-150">{t('priv.updated')}</p>
      <h3 className="text-base font-semibold text-white" data-id="element-151">
        {t('priv.1.title')}
      </h3>
      <p data-id="element-152">{t('priv.1.desc')}</p>
      <h3 className="text-base font-semibold text-white" data-id="element-153">
        {t('priv.2.title')}
      </h3>
      <p data-id="element-154">{t('priv.2.desc')}</p>
      <h3 className="text-base font-semibold text-white" data-id="element-155">
        {t('priv.3.title')}
      </h3>
      <p data-id="element-156">{t('priv.3.desc')}</p>
      <h3 className="text-base font-semibold text-white" data-id="element-157">
        {t('priv.4.title')}
      </h3>
      <p data-id="element-158">{t('priv.4.desc')}</p>
      <h3 className="text-base font-semibold text-white" data-id="element-159">
        {t('priv.5.title')}
      </h3>
      <p data-id="element-160">{t('priv.5.desc')}</p>
      <h3 className="text-base font-semibold text-white" data-id="element-161">
        {t('priv.6.title')}
      </h3>
      <p data-id="element-162">{t('priv.6.desc')}</p>
    </>;
}
function TermsContent() {
  const {
    t
  } = useLanguage();
  return <>
      <p className="text-gray-400 text-xs" data-id="element-163">{t('terms.updated')}</p>
      <h3 className="text-base font-semibold text-white" data-id="element-164">
        {t('terms.1.title')}
      </h3>
      <p data-id="element-165">{t('terms.1.desc')}</p>
      <h3 className="text-base font-semibold text-white" data-id="element-166">
        {t('terms.2.title')}
      </h3>
      <p data-id="element-167">{t('terms.2.desc')}</p>
      <h3 className="text-base font-semibold text-white" data-id="element-168">
        {t('terms.3.title')}
      </h3>
      <p data-id="element-169">{t('terms.3.desc')}</p>
      <h3 className="text-base font-semibold text-white" data-id="element-170">
        {t('terms.4.title')}
      </h3>
      <p data-id="element-171">{t('terms.4.desc')}</p>
      <h3 className="text-base font-semibold text-white" data-id="element-172">
        {t('terms.5.title')}
      </h3>
      <p data-id="element-173">{t('terms.5.desc')}</p>
      <h3 className="text-base font-semibold text-white" data-id="element-174">
        {t('terms.6.title')}
      </h3>
      <p data-id="element-175">{t('terms.6.desc')}</p>
    </>;
}